import { useContext } from 'react';
import { useMachine, useSelector } from '@xstate/react';
import { DataAccessContext } from '@chargify/data-access';
import { usePaymentsForm } from '@chargify/payments-sdk';

import { useComponentSettings } from '../useComponentSettings';
import { useServices } from '../useServices';

import { serializeErrors } from './serializeErrors';
import { serializeValidationSchema } from '../../machines/createPaymentProfile';
import {
  createPaymentProfile,
  paymentGatewaySelectors,
  paymentProfileSelectors,
  PaymentMethod,
} from '../../machines';
import { PaymentProfilesContext } from '../../contexts';
import { ReplacePaymentProfilesSuccessEvent } from '../../machines/createPaymentProfile/types';

type Options = {
  catalogId: number;
  formElementId: string;
  subscriptionId: number;
  paymentMethodType: PaymentMethod;
  onCreateSuccess: () => void;
  activePaymentProfileId: number;
  isActivePaymentMethod: boolean;
};

/**
 * Configures machine responsible for creating payment profile
 * @param {string} formElementId - HTML element used to tokenize payment method
 * @param {number} activePaymentProfileId - ID of active payment profile
 * @param {PaymentMethod} paymentMethodType - type of payment method
 * @param {string} formElementId - HTML element used to tokenize payment method
 * @param {() => void} onCreateSuccess - callback to be called when payment profile is created
 * @returns {Record} payment profile service
 */
export const useCreatePaymentProfile = ({
  formElementId,
  paymentMethodType = 'creditCard',
  onCreateSuccess,
  activePaymentProfileId,
  isActivePaymentMethod,
  subscriptionId,
}: Options) => {
  const { paymentProfile: paymentProfileConfig } = useComponentSettings();
  const paymentsForm = usePaymentsForm();
  const { paymentGatewayService, subscriptionsService } = useServices();
  const { paymentProfilesService } = useContext(PaymentProfilesContext);
  const { dataAccess } = useContext(DataAccessContext);

  const gatewayRequiredFields = useSelector(
    paymentGatewayService,
    paymentGatewaySelectors.getRequiredFieldsConfig
  );

  const requiredFields =
    paymentProfileConfig.requiredFields ??
    gatewayRequiredFields[paymentMethodType];

  const [, sendToMachine, service] = useMachine(createPaymentProfile, {
    context: {
      requiredFields,
      validationSchema: serializeValidationSchema(requiredFields),
    },
    actions: {
      onCreateProfileSuccess: ({ paymentProfiles, createdProfile }) => {
        onCreateSuccess();
        paymentProfilesService.send({
          type: 'ADD_PAYMENT_PROFILE',
          paymentProfileId: createdProfile.id,
          paymentProfile: createdProfile,
        });

        if (isActivePaymentMethod) {
          paymentProfilesService.send({
            type: 'CHANGE_PAYMENT_PROFILE',
            paymentProfileId: createdProfile.id,
          });
          dataAccess.updateSubscriptionPaymentProfile(
            createdProfile.id,
            subscriptionId
          );
        }
        if (
          paymentProfiles.idsForReplacement.includes(activePaymentProfileId)
        ) {
          paymentProfilesService.send({
            type: 'CHANGE_PAYMENT_PROFILE',
            paymentProfileId: createdProfile.id,
          });
        }
      },
      onReplacePaymentProfilesSuccess: (
        ctx,
        event: ReplacePaymentProfilesSuccessEvent
      ) => {
        event.data.subscriptions.success.forEach((subscription) => {
          subscriptionsService.send({
            type: 'UPDATE_SUBSCRIPTION',
            subscriptionId: subscription.id,
            catalogId: subscription.product.productFamilyId,
            billing: {
              paymentProfileConnected: true,
              ...ctx.createdProfile,
            },
          });
        });
      },
    },
    services: {
      validatePaymentMethod: (ctx) =>
        new Promise((resolve, reject) => {
          const { billingAddress, paymentMethod, validationSchema } = ctx;
          try {
            validationSchema.validateSync(
              {
                billingAddress: billingAddress.values,
                paymentMethod: paymentMethod.values,
              },
              { abortEarly: false }
            );
            resolve({});
          } catch (error) {
            const errors = serializeErrors(error);
            reject({ errors });
          }
        }),
      tokenizePaymentMethod: () =>
        new Promise((resolve, reject) => {
          paymentsForm.token(
            document.getElementById(formElementId),
            (token) => resolve(token),
            (error) => reject(error)
          );
        }),
      createPaymentProfile: (ctx) => dataAccess.createPaymentProfile(ctx.token),
      replacePaymentProfiles: (ctx) => {
        const { id: paymentProfileId } = ctx.createdProfile;

        return dataAccess.replacePaymentProfiles(
          ctx.paymentProfiles.idsForReplacement,
          paymentProfileId
        );
      },
    },
  });

  const isProcessing = useSelector(
    service,
    paymentProfileSelectors.getPaymentProfileProcessing
  );

  const isProfileConnectionError = useSelector(
    service,
    paymentProfileSelectors.getProfileConnectionError
  );

  return {
    isProcessing,
    isProfileConnectionError,
    savePaymentMethod: () => sendToMachine({ type: 'SAVE_PAYMENT_METHOD' }),
    service,
  };
};
